import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/lib/auth";
import { ClientProvider } from "@/lib/clientContext";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import PracticeSetupPanel from "@/components/setup/PracticeSetupPanel";
import PaymentPosting from "@/pages/PaymentPosting";
import CheckLedger from "@/pages/CheckLedger";
import CheckLedgerV2 from "@/pages/CheckLedgerV2";
import EraWorkspaceV2 from "@/pages/EraWorkspaceV2";
import UploadEob from "@/pages/UploadEob";
import ViewEra from "@/pages/ViewEra";
import RevQ from "@/pages/RevQ";
import Placeholder from "@/pages/Placeholder";
import Claims from "@/pages/sections/Claims";
import Encounters from "@/pages/sections/Encounters";
import Superbills from "@/pages/sections/Superbills";
import PatientBilling from "@/pages/sections/PatientBilling";
import Eligibility from "@/pages/sections/Eligibility";
import Authorizations from "@/pages/sections/Authorizations";
import Recommendations from "@/pages/sections/Recommendations";
import Analytics from "@/pages/sections/Analytics";
import Rules from "@/pages/sections/Rules";
import ModuleStatus from "@/pages/sections/ModuleStatus";
import Team from "@/pages/sections/Team";
import CreateClaimsV2 from "@/pages/sections/CreateClaimsV2";
import ChartShapesDebug from "@/pages/ChartShapesDebug";
import LearningCenter from "@/pages/LearningCenter";
import LuminaDocuments from "@/pages/sections/LuminaDocuments";
import LuminaAtWork from "@/pages/LuminaAtWork";
import PatientDocuments from "@/pages/sections/PatientDocuments";
import ClinicDocuments from "@/pages/sections/ClinicDocuments";
import Telemedicine from "@/pages/sections/Telemedicine";
import TelehealthV2 from "@/pages/sections/TelehealthV2";
import Dictation from "@/pages/sections/Dictation";
import PatientsDirectory from "@/pages/sections/PatientsDirectory";
import EncounterReview from "@/pages/sections/EncounterReview";
import Denials from "@/pages/sections/Denials";
import PatientsWorkspace from "@/pages/sections/PatientsWorkspace";
import Attachments from "@/pages/sections/Attachments";
import Migration from "@/pages/sections/Migration";
import Dictate from "@/pages/Dictate";
import Chart from "@/pages/Chart";
import ChartV2 from "@/pages/ChartV2";
import PatientsWorkspaceV2 from "@/pages/sections/PatientsWorkspaceV2";
import Settings from "@/pages/Settings";
import BaaGate from "@/components/BaaGate";
import VerificationGate from "@/components/VerificationGate";

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <BaaGate />
        <VerificationGate />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/dictate" element={<Dictate />} />
          <Route path="/chart" element={<ClientProvider><Chart /></ClientProvider>} />
          <Route path="/chart-v2" element={<ClientProvider><ChartV2 /></ClientProvider>} />
          <Route path="/chart/:chartUuid" element={<ClientProvider><ChartV2 /></ClientProvider>} />
          <Route path="/chart-shapes-debug" element={<ChartShapesDebug />} />
          <Route path="/LearningCenter" element={<LearningCenter />} />
          <Route path="/learning-center" element={<Navigate to={`/LearningCenter${window.location.hash}`} replace />} />
          <Route element={<AppShell />}>
            <Route path="/home" element={<Dashboard />} />
            <Route path="/patients" element={<PatientsDirectory />} />
            <Route path="/dashboard" element={<Navigate to={`/home${window.location.search}`} replace />} />
            <Route path="/setup" element={<Navigate to="/settings" replace />} />
            <Route path="/payments" element={<Navigate to="/payments-v2" replace />} />
            <Route path="/payments/legacy" element={<PaymentPosting />} />
            <Route path="/payments/upload" element={<UploadEob />} />
            <Route path="/payments/era/:fileId" element={<ViewEra />} />
            <Route path="/payments-v2" element={<CheckLedgerV2 />} />
            <Route path="/payments-v2/:fileId" element={<EraWorkspaceV2 />} />
            <Route path="/revq" element={<RevQ />} />

            {/* Work */}
            <Route path="/create-claims" element={<CreateClaimsV2 />} />
            <Route path="/lumina-documents" element={<LuminaDocuments />} />
            <Route path="/luminaATwork" element={<LuminaAtWork />} />
            <Route path="/patient-documents" element={<PatientDocuments />} />
            <Route path="/clinic-documents" element={<ClinicDocuments />} />
            <Route path="/telemedicine" element={<Telemedicine />} />
            <Route path="/telehealth-v2" element={<TelehealthV2 />} />
            <Route path="/dictation" element={<Dictation />} />
            <Route path="/encounter-review" element={<EncounterReview />} />
            <Route path="/create-claims-v2" element={<Navigate to="/create-claims" replace />} />
            <Route path="/eligibility" element={<Eligibility />} />
            <Route path="/authorizations" element={<Authorizations />} />
            <Route path="/denials" element={<Denials />} />
            <Route path="/patient-billing" element={<PatientBilling />} />

            {/* Records */}
            <Route path="/records/claims" element={<Claims />} />
            <Route path="/records/encounters" element={<Encounters />} />
            <Route path="/records/superbills" element={<Superbills />} />
            <Route path="/records/patients" element={<PatientsWorkspaceV2 />} />
            <Route path="/records/patients-v2" element={<Navigate to="/records/patients" replace />} />

            {/* Insights */}
            <Route path="/recommendations" element={<Recommendations />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/rules" element={<Rules />} />

            {/* System */}
            <Route path="/team" element={<Team />} />
            <Route path="/modules" element={<ModuleStatus />} />
            <Route path="/attachments" element={<Attachments />} />
            <Route path="/data-migration" element={<Migration />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/practice-setup-v1" element={<div className="px-7 py-6"><PracticeSetupPanel /></div>} />
          </Route>
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors closeButton offset={{ bottom: 80 }} />
    </AuthProvider>
  );
}
